// Ключ КЕП без залежностей від Node: той самий код у CLI (lib/sign.js) і в браузері
// (web/src/kep.js). Транспорт для CMP/OCSP/TSP (query) і читання файлів — у викликача.
import gost89 from "gost89";
import { Box, Certificate, models, rfc3161, dstszi2010 } from "jkurwa";
import { Buffer } from "buffer";
import { decryptDstuPfx, kupynaKeyId } from "./dstu-pfx.js";

// CMP-сервери відомих АЦСК/КНЕДП — по них шукаємо сертифікат до ключа
export const DEFAULT_CMP = [
  ["http://ca.vchasno.ua/services/cmp/", "Вчасно"],
  ["http://acskidd.gov.ua/services/cmp/", "ІДД ДПС (податкова)"],
  ["http://ca.diia.gov.ua/services/cmp/", "Дія"],
  ["http://acsk.privatbank.ua/services/cmp/", "ПриватБанк"],
  ["http://masterkey.ua/services/cmp/", "MasterKey (Арт-Мастер)"],
  ["http://uakey.com.ua/services/cmp/", "УСЦ (uakey)"],
  ["http://ca.informjust.ua/services/cmp/", "Інформ'юст"],
  ["http://csk.uz.gov.ua/services/cmp/", "Укрзалізниця"],
  ["http://ca.mil.gov.ua/services/cmp/", "Міноборони"],
  ["http://ca.monobank.ua/services/cmp/", "monobank (Універсал Банк)"],
];

export const hasSigningCert = (box) => box.keys.some((k) => k.priv && k.cert);

// keyBuffer — файл ключа; certBuffers — .cer поруч із ключем (необов'язково)
export function openKey({ keyBuffer, password, certBuffers = [], query = null }) {
  const box = new Box({ algo: gost89.compat.algos(), query });
  try {
    box.load({ keyBuffers: [Buffer.from(keyBuffer)], password });
  } catch (e) {
    // Новий формат (ДСТУ 7624/7564) jkurwa не розшифровує — відкриваємо самі й даємо їй готові PKCS#8
    const plain = decryptDstuPfx(new Uint8Array(keyBuffer), password);
    if (!plain.length) throw e;
    let loaded = 0;
    for (const der of plain) {
      try { for (const priv of models.Priv.from_asn1(Buffer.from(der), true).keys) { box.load({ priv }); loaded++; } } catch { /* ключ непідтримуваного алгоритму — пропускаємо */ }
    }
    if (!loaded) throw new Error("Ключ нового формату розшифровано, але алгоритм ключа не підтримується");
    // Ідентифікатор такого ключа в ЦСК — «Купина»-256 від відкритого ключа (як localKeyId у файлі), не ГОСТ 34.311
    box.loadCertsCmp = (url) => loadCertsCmpKupyna(box, url);
  }
  for (const c of certBuffers) box.load({ certPem: Buffer.from(c) });
  return box;
}

// Запит CMP як у jkurwa (services/cmp.js), але з keyid «Купини»; повертає, скільки ключів отримали сертифікат
function loadCertsCmpKupyna(box, url) {
  const ids = box.keys.filter((k) => k.priv).map((k) => Buffer.from(kupynaKeyId(k.priv.pub().serialize())));
  const ct = Buffer.alloc(120);
  ids[0].copy(ct, 0xc); (ids[1] || ids[0]).copy(ct, 0x2c);
  ct[0x6c] = 1; ct[0x70] = 1; ct[0x08] = 2; ct[0] = 0x0d;
  const payload = new models.Message({ type: "data", data: ct }).as_asn1();
  return new Promise((resolve, reject) => box.query("POST", url, { "Content-Length": payload.length }, payload, (resp, status) => {
    try {
      if (status !== 200) throw new Error(`CMP ${status}`);
      const msg = new models.Message(resp);
      if (msg.info.readInt32LE(4) !== 1) return resolve(0);
      for (const der of new models.Message(msg.info.slice(8)).info.certificate) box.add({ cert: new Certificate(der) });
      box._indexKeys();
      resolve(box.keys.filter((k) => k.priv && k.cert).length);
    } catch (e) { reject(e); }
  }));
}

// Паралельний пошук сертифіката по всіх АЦСК; повертає перший, де знайшовся
export async function findCertsAcrossCAs(box, urls = DEFAULT_CMP, log = () => {}) {
  log(`Шукаю сертифікат до ключа по ${urls.length} АЦСК…`);
  const results = await Promise.all(urls.map(async ([url, name]) => {
    try {
      const n = await box.loadCertsCmp(url);
      if (n) log(`  ✓ ${name || url}: знайдено ${n} серт.`);
      return { url, name, n };
    } catch {
      return { url, name, n: 0 };
    }
  }));
  return results.find((r) => r.n > 0) || null;
}

// Коди власника сертифіката: зі старого розширення ipn або, якщо його немає (сертифікати за ETSI EN 319 412,
// як у КНЕДП monobank), — з полів суб'єкта organizationIdentifier «NTRUA-ЄДРПОУ» і serialNumber «TINUA-РНОКПП»
export function certIds(cert) {
  const ipn = cert?.extension?.ipn || {}, subj = cert?.subject || {};
  const pick = (v, re) => (re.exec(String(v || "")) || [])[1] || null;
  return {
    edrpou: ipn.EDRPOU ? String(ipn.EDRPOU) : pick(subj.organizationIdentifier, /^NTRUA-(\d{8})$/),
    drfo: ipn.DRFO ? String(ipn.DRFO) : pick(subj.serialNumber, /^TINUA-(\d{10})$/),
  };
}

export function idsFromBox(box) {
  let edrpou, drfo;
  for (const k of box.keys) {
    if (!k.cert) continue;
    const ids = certIds(k.cert);
    if (ids.edrpou) edrpou = ids.edrpou;
    if (ids.drfo) drfo = ids.drfo;
  }
  return { edrpou: edrpou || drfo, drfo: drfo || edrpou };
}

// CMS-підпис рядка з вкладеним вмістом, base64
export async function signText(box, text) {
  const signed = await box.pipe(Buffer.from(String(text), "utf8"), [{ op: "sign" }], null);
  return Buffer.from(signed).toString("base64");
}

// Транспортний заголовок ДПС, як у кабінеті (signUiService.addSignHeaderDp): «UA1_SIGN\0» + довжина
// підпису (4 байти, little-endian) + CMS. Без нього POST doc/inv/p7s відповідає «Помилка перевірки підпису: null»
const SIGN_HEADER = Buffer.from("UA1_SIGN\0", "latin1");
export function wrapSignHeader(der) {
  const len = Buffer.alloc(4);
  len.writeUInt32LE(der.length, 0);
  return Buffer.concat([SIGN_HEADER, len, Buffer.from(der)]);
}
// Зворотне: CMS із контейнера (або як є, якщо заголовка немає)
export function unwrapSignHeader(buf) {
  const b = Buffer.from(buf);
  if (!b.subarray(0, SIGN_HEADER.length).equals(SIGN_HEADER)) return b;
  const len = b.readUInt32LE(SIGN_HEADER.length);
  return b.subarray(SIGN_HEADER.length + 4, SIGN_HEADER.length + 4 + len);
}

// Підпис документа для подання в кабінет (doc/inv/p7s), як робить сам кабінет: CAdES-T (мітки часу TSP на
// вміст і на підпис), вкладені дані й сертифікат, транспортний заголовок UA1_SIGN, base64.
// Мітку дає TSP-сервер із сертифіката (через query — у браузері наш проксі)
export async function signBytes(box, bytes, { tsp = "all" } = {}) {
  let der;
  try {
    der = await box.pipe(Buffer.from(bytes), [{ op: "sign", tsp }], null);
  } catch (e) {
    // jkurwa відхиляє з null, якщо TSP-сервер не дав мітку
    throw new Error(e ? (e.message || String(e)) : "Не вдалося отримати мітку часу (TSP) від надавача — спробуйте ще раз");
  }
  return wrapSignHeader(Buffer.from(der)).toString("base64");
}

// Мітка часу TSP на готовий хеш ГОСТ 34.311 (як tspService.getStamp у jkurwa — він не експортується)
function stampHash(box, cert, hashB) {
  const req = rfc3161.TimeStampReq.encode({ version: 1, messageImprint: { hashAlgorithm: { algorithm: "Gost34311" }, hashedMessage: hashB } }, "der");
  return new Promise((resolve, reject) => box.query("POST", cert.extension.subjectInfoAccess.link,
    { "Content-Type": "application/tsp-request", "Content-Length": req.length }, req, (full) => {
      if (!full) return reject(new Error("Не вдалося отримати мітку часу (TSP) від надавача — спробуйте ще раз"));
      const r = rfc3161.TimeStampResp.decode(full, "der");
      if (r.status.status !== "granted") return reject(new Error("Надавач відмовив у мітці часу (TSP)"));
      resolve(dstszi2010.ContentInfo.encode(r.timeStampToken, "der"));
    }));
}

// Підпис готового хешу, як signUiService.signDataHash у кабінеті (IIT SignHash з appendCert): відокремлений CMS
// (без вкладених даних), messageDigest = переданий хеш, сертифікат усередині, base64 без заголовка UA1_SIGN.
// Так кабінет реєструє отримані РК (b2b/docs/{id}/p7s_hash). Хеш — base64 ГОСТ 34.311 (32 байти)
export async function signHash(box, hashB64, { tsp = "all" } = {}) {
  const dataHash = Buffer.from(String(hashB64).trim(), "base64");
  if (dataHash.length !== 32) throw new Error(`Кабінет дав хеш несподіваної довжини (${dataHash.length} байт) — підпис ГОСТ 34.311 неможливий`);
  const key = box.keyFor("sign");
  const tspB = tsp === "all" || tsp === "content" ? await stampHash(box, key.cert, dataHash) : undefined;
  const message = new models.Message({ type: "signedData", cert: key.cert, data: null, dataHash, signer: key.priv, hash: box.algo.hash, tspB });
  if (tsp === "all" || tsp === "signature") message.addSignatureToken(await stampHash(box, key.cert, box.algo.hash(message.signature)));
  return Buffer.from(message.as_asn1()).toString("base64");
}

// Що всередині підпису хешу (для перевірки на сервері перед надсиланням у кабінет): підписаний хеш і коди підписувача
export function hashSignatureInfo(b64) {
  const msg = new models.Message(Buffer.from(String(b64), "base64"));
  const c = msg.info.certificate?.[0], ipn = c ? new Certificate(c).extension?.ipn : null;
  return { digest: Buffer.from(msg.pattrs.messageDigest || []).toString("base64"), edrpou: ipn?.EDRPOU || null, drfo: ipn?.DRFO || null };
}

// Підписаний ЄДРПОУ: «пароль» OAuth кабінету і заголовок для /ws/public_api/
export const signedAuthHeader = (box, taxId) => signText(box, taxId);
